import React, { memo, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useCheckFlagAccessControl } from "../../../../../../../../hooks";
import {
	StyledGrid,
	StyledRow,
	Colors,
} from "../../../../../../../../components/common";
import Style, {
	StyledGridItem,
} from "../../../../../../../../components/common/styles";
import { TabAccessNames, TabKeys } from "../../constants/access";
import { OrderFormProps } from "../../types/FormProps";
import { Primary, Gray } from "../styled";
import { useOrderModalContext } from "../../../../../context";

import OutOfTown from "./components/OutOfTown";
import AdditionalCost from "./components/AdditionalCost";
import OrderHourly from "./components/OrderHourly";
import OrderPrice from "./components/Price";
import OrderDate from "./components/OrderDate";
import ArrivalTime from "./components/ArrivalTime";
import ExecutionTime from "./components/ExecutionTime";
import TimeWaitingAndIdle from "./components/TimeWaitingAndIdle";

const BaseSummary: React.FC<BaseSummary.Props> = ({
	tab,
	form,
	disabled,
	vertical,
}) => {
	const { t } = useTranslation();
	const { points } = useOrderModalContext();
	const checkFlagAccessControl = useCheckFlagAccessControl(TabAccessNames);

	const styles = useMemo<Style.GridType>(() => {
		if (vertical) {
			return {
				areas: `'created'
						'arrival_time'
						'execution'
						'route_length'
						'additionalCost'
						'idleTime'
						'waitingTime'
						'hourly'
						'out_of_town'
						'price'
						`,
				columns: "repeat(1, 1fr)",
				rows: "repeat(9, 24px)",
			};
		}
		return {
			areas: `'created arrival_time arrival_time execution'
					'route_length out_of_town waitingTime idleTime'
					'price price additional_cost hourly'`,
			columns: "repeat(4, 1fr)",
			rows: "repeat(3, 20px)",
		};
	}, [vertical]);

	const nextElementById = useCallback(
		(elemId: string): HTMLElement | null => {
			const elem = document.getElementById(elemId);
			if (elem) {
				elem.focus();
				if (elem?.nodeName === "INPUT") {
					const input = elem as HTMLInputElement;
					input?.select();
				}
			}
			return elem;
		},
		[],
	);

	const pointsLength = useMemo(() => !!points?.length, [points?.length]);

	/**
	 * Display total route length (km), aligned with server
	 * `cost.distances.total` + `suburbanDistanceOverride` rules:
	 * - geometry G = route.distance (A→B)
	 * - geometric suburban from route.suburbanPriceZones
	 * - settlement = G − geometric suburban
	 * - shrink=true  → total = overrideKm + settlementKm
	 * - shrink=false → total = max(G, overrideKm + settlementKm)
	 * Without a real override → cost.distances.total or route.distance.
	 */
	const distance = useMemo(() => {
		// --- old ---
		// const value = tab.form?.distance || tab.state?.route?.distance || 0;
		// return Number((value / 1000).toFixed(3));

		const routeDistanceM = Number(tab.state?.route?.distance);
		const formDistanceM = Number(tab.form?.distance);
		let geometryTotalM = 0;
		if (Number.isFinite(routeDistanceM) && routeDistanceM > 0) {
			geometryTotalM = routeDistanceM;
		} else if (Number.isFinite(formDistanceM) && formDistanceM > 0) {
			geometryTotalM = formDistanceM;
		}

		const routeSuburbanKm = Number(tab.state?.route?.suburbanPriceZones);
		const geometrySuburbanM =
			Number.isFinite(routeSuburbanKm) && routeSuburbanKm >= 0
				? routeSuburbanKm * 1000
				: geometryTotalM;
		const geometrySettlementM = Math.max(
			0,
			geometryTotalM - geometrySuburbanM,
		);

		const overrideKm = Number(tab.form?.suburbanDistanceOverride) || 0;
		const overrideShrink = !!tab.form?.suburbanDistanceOverrideState;
		const isDummyOverride = overrideKm === 0 && !overrideShrink;

		if (!isDummyOverride) {
			const suburbanM = Math.max(0, overrideKm) * 1000;
			if (overrideShrink) {
				// set: suburban = value; city kept from geometry; total = sum
				return Number(
					((suburbanM + geometrySettlementM) / 1000).toFixed(3),
				);
			}
			// add: never below geometric A→B
			let totalM = suburbanM + geometrySettlementM;
			if (geometryTotalM > 0 && totalM < geometryTotalM) {
				totalM = geometryTotalM;
			}
			return Number((totalM / 1000).toFixed(3));
		}

		const baseM =
			Number.isFinite(formDistanceM) && formDistanceM > 0
				? formDistanceM
				: geometryTotalM;
		return Number((baseM / 1000).toFixed(3));
	}, [
		tab.form?.distance,
		tab.form?.suburbanDistanceOverride,
		tab.form?.suburbanDistanceOverrideState,
		tab.state?.route?.distance,
		tab.state?.route?.suburbanPriceZones,
	]);

	const setEditableParam = useCallback(
		(param: typeof tab.state.modals.editableParam) => {
			tab.setState((prev) => ({
				...prev,
				modals: {
					...prev.modals,
					editableParam: disabled ? null : param,
				},
			}));
			if (!param) {
				form.setValue("orderSave", true);
				form.setValue("orderPointSave", false);
				nextElementById("driver-node-id");
			}
		},
		[disabled, form, nextElementById, tab],
	);

	return (
		<StyledGrid
			{...styles}
			gap=".2rem 1rem"
			w="100%"
			p="6px 12px"
			bgC={Colors.BACKGROUND_GRAY_2}
			b={{ bottom: `1.5px solid ${Colors.BORDER_GRAY}` }}
		>
			<StyledGridItem area="created" alignSelf="center">
				<OrderDate form={form} tab={tab} disabled={disabled} />
			</StyledGridItem>

			<StyledGridItem area="arrival_time" alignSelf="center">
				<ArrivalTime form={form} tab={tab} disabled={disabled} />
			</StyledGridItem>

			<StyledGridItem area="execution" alignSelf="center">
				<ExecutionTime form={form} tab={tab} disabled={disabled} />
			</StyledGridItem>

			<StyledGridItem area="route_length" alignSelf="center">
				<StyledRow gap="6px">
					<Primary>
						{`${t([
							`orderPage.order_form.route_length`,
							"Distance",
						])}:`}
					</Primary>

					<Gray>
						{distance} {t(`units.km`) ?? "km"}
					</Gray>
				</StyledRow>
			</StyledGridItem>

			<StyledGridItem area="out_of_town" alignSelf="center">
				<OutOfTown
					disabled={
						disabled ||
						!checkFlagAccessControl[TabKeys.OUT_OF_TOWN_DISTANCE]
					}
					form={form}
					tab={tab}
					edit={
						tab.state?.modals?.editableParam ===
							"suburbanDistanceOverride" && pointsLength
					}
					setEdit={(edit) =>
						setEditableParam(
							edit ? "suburbanDistanceOverride" : null,
						)
					}
				/>
			</StyledGridItem>

			<StyledGridItem area="price" alignSelf="center">
				<OrderPrice
					key="price"
					disabled={
						disabled || !checkFlagAccessControl[TabKeys.PRICE]
					}
					form={form}
					edit={
						tab.state.modals.editableParam === "price" &&
						pointsLength
					}
					setEdit={(edit) => setEditableParam(edit ? "price" : null)}
					tab={tab}
				/>
			</StyledGridItem>

			<StyledGridItem area="idleTime" alignSelf="center">
				<TimeWaitingAndIdle
					id="idleTime"
					form={form}
					tab={tab}
					title={t([`orderPage.order_form.idle_time`, "Plain"])}
					disabled={
						disabled || !checkFlagAccessControl[TabKeys.IDLE_TIME]
					}
					edit={
						tab.state.modals.editableParam === "idleTime" &&
						pointsLength
					}
					setEdit={(edit) =>
						setEditableParam(edit ? "idleTime" : null)
					}
				/>
			</StyledGridItem>

			<StyledGridItem area="waitingTime" alignSelf="center">
				<TimeWaitingAndIdle
					id="waitingTime"
					form={form}
					tab={tab}
					title={t([
						`orderPage.order_form.waiting_time`,
						"Expectation",
					])}
					disabled={
						disabled ||
						!checkFlagAccessControl[TabKeys.AWAITING_TIME]
					}
					edit={
						tab.state.modals.editableParam === "waitingTime" &&
						pointsLength
					}
					setEdit={(edit) =>
						setEditableParam(edit ? "waitingTime" : null)
					}
				/>
			</StyledGridItem>

			<StyledGridItem area="additional_cost" alignSelf="center">
				<AdditionalCost
					disabled={
						disabled ||
						!checkFlagAccessControl[TabKeys.ADDITIONAL_PRICE]
					}
					form={form}
					tab={tab}
					edit={
						tab.state.modals.editableParam === "additionalCost" &&
						pointsLength
					}
					setEdit={(edit) =>
						setEditableParam(edit ? "additionalCost" : null)
					}
				/>
			</StyledGridItem>

			<StyledGridItem area="hourly" alignSelf="center">
				<OrderHourly
					disabled={
						disabled || !checkFlagAccessControl[TabKeys.HOURLY_TIME]
					}
					form={form}
					edit={
						tab.state?.modals?.editableParam === "hourly" &&
						pointsLength
					}
					setEdit={(edit) => setEditableParam(edit ? "hourly" : null)}
					tab={tab}
				/>
			</StyledGridItem>
		</StyledGrid>
	);
};

declare namespace BaseSummary {
	interface Props extends OrderFormProps {
		vertical?: boolean;
	}
}
export const Summary = memo(BaseSummary);
export default BaseSummary;
